import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { Dialog } from "../me/index.js";
import { useDialogWege } from "./dialogWege.js";
/**
 * Die Verwerfen-Rückfrage als SUBDIALOG (Entscheid Dirk 47, 08.10.2026) — statt des
 * Browser-`confirm`, das bis zur ME-Etappe 3 an dieser Stelle stand.
 *
 * Muster ist die Story „Dialog › VerwerfenRueckfrage“ aus me-design: ein zweiter Dialog,
 * `unter="stapel"`, 480 Pixel, zwei Knöpfe „Weiter bearbeiten“ und „Verwerfen“; der
 * Eltern-Dialog tritt zurück und ist solange gesperrt (`inert`). Escape heisst hier
 * „Weiter bearbeiten“ — der Weg, auf dem nichts verloren geht.
 *
 * Der Wortlaut bleibt der bisherige („Hier stehen ungespeicherte Änderungen …“), es ist
 * derselbe Verlust wie beim Verlassen-Wächter des Routers.
 *
 * Warum ein Modulzustand und kein Kontext: `darfAbbrechen` wird an 40 Stellen in web aus
 * einem Abbrechen-Rückruf heraus gerufen — als Funktion, nicht als Baustein. Die Frage
 * stellt deshalb das gerade OBERSTE `Modal` (das zuletzt geöffnete; es bindet dafür
 * `RueckfrageGastgeber` ein). Gezeichnet wird sie per Portal an `document.body` und legt
 * sich im (dokumentweiten) Stapel der Dialoge über die Maske.
 */
const WORTLAUT = "Hier stehen ungespeicherte Änderungen. Sie gehen verloren. Wirklich abbrechen?";
let offen = null;
/** Die eingebundenen Gastgeber (je offenem Modal einer), mit ihrer Öffnungsnummer. */
const gastgeber = new Set();
const hoerer = new Set();
let naechsteNummer = 0;
function melden() {
    for (const h of hoerer)
        h();
}
function abonnieren(h) {
    hoerer.add(h);
    return () => { hoerer.delete(h); };
}
/** Wer die Frage zeichnet: der zuletzt geöffnete Gastgeber — oder niemand. */
function zeichner() {
    let hoechste = null;
    for (const n of gastgeber)
        if (hoechste === null || n > hoechste)
            hoechste = n;
    return hoechste;
}
function beantworten(verwerfen) {
    const anfrage = offen;
    if (anfrage === null)
        return;
    offen = null;
    anfrage.aufloesen(verwerfen);
    melden();
}
/**
 * Die Rückfrage vor dem Abbrechen — an einer Stelle für die ganze Anwendung.
 *
 * Liefert `true`, wenn wirklich abgebrochen werden darf: weil nichts Ungespeichertes darin
 * steht (dann ohne Frage), oder weil „Verwerfen“ gewählt wurde. „Weiter bearbeiten“
 * und Escape liefern `false` (ein Kreuz hat die strikte Rückfrage nicht, Entscheid 26).
 *
 * Asynchron seit ME-Etappe 3 (die Frage ist ein Dialog, kein blockierendes `confirm`
 * mehr): Aufrufer schreiben `if (!(await darfAbbrechen(geaendert))) return;`.
 *
 * Steht schon eine Frage offen, kommt keine zweite dazu (Antwort `false`). Steht gar kein
 * `Modal` (kommt in der Anwendung nicht vor — alle Aufrufer sitzen im Abbrechen eines
 * offenen Modals), bleibt als Notweg das Browser-`confirm`.
 */
export function darfAbbrechen(geaendert) {
    if (!geaendert)
        return Promise.resolve(true);
    if (offen !== null)
        return Promise.resolve(false);
    if (zeichner() === null)
        return Promise.resolve(window.confirm(WORTLAUT));
    return new Promise((aufloesen) => {
        offen = { aufloesen };
        melden();
    });
}
/**
 * Bindet die Rückfrage in ein offenes `Modal` ein. Gezeichnet wird sie nur vom zuletzt
 * geöffneten Gastgeber. Verschwindet der letzte Gastgeber mit offener Frage, gilt sie als
 * mit „Weiter bearbeiten“ beantwortet — die Maske, deren Abbrechen sie schützte, ist
 * ohnehin fort.
 */
export function RueckfrageGastgeber() {
    const [nummer] = useState(() => ++naechsteNummer);
    useEffect(() => {
        gastgeber.add(nummer);
        melden();
        return () => {
            gastgeber.delete(nummer);
            if (gastgeber.size === 0)
                beantworten(false);
            else
                melden();
        };
    }, [nummer]);
    const zeigen = useSyncExternalStore(abonnieren, () => offen !== null && zeichner() === nummer);
    if (!zeigen)
        return null;
    /*
     * Per Portal an `document.body`, als GESCHWISTER der Maske (wie in der Story) — nicht in
     * ihrem Inhalt (Review Aufgabe 1, K1/K2): Die Maske darunter bekommt von me-dialog
     * `data-hinten` (`pointer-events: none` am Blatt) bzw. am Telefon `data-versteckt`
     * (`display: none`). Beides erbt sich über den Flat Tree in alles, was in ihrem Inhalt
     * steht — die Knöpfe der Frage wären mit Maus und Finger nicht zu treffen, am Telefon
     * gar nicht zu sehen. Der Stapel von me-dialog ist dokumentweit; die Frage liegt also
     * auch als Geschwister über der Maske, und die Fokus-Rückgabe geht an den Auslöser.
     */
    return createPortal(_jsx(VerwerfenRueckfrage, { onVerwerfen: () => beantworten(true), onWeiterBearbeiten: () => beantworten(false) }), document.body);
}
/** Der Subdialog selbst — nach der Story „Dialog › VerwerfenRueckfrage“. */
export function VerwerfenRueckfrage(p) {
    const anker = useRef(null);
    const weiter = useRef(null);
    const steuerung = useRef(null);
    useDialogWege(anker, {
        // Escape und natives Schliessen schliessen die Frage selbst (kein Widerspruch) — sie
        // heissen „Weiter bearbeiten“; die Antwort kommt mit dem Schliessen.
        beiAbbrechen: () => { },
        beiGeschlossen: () => p.onWeiterBearbeiten(),
    });
    // Im selben Commit öffnen (wie Modal), dann bekommt der sichere Knopf den Fokus: Ein
    // reflexhaftes Enter verwirft nichts.
    useLayoutEffect(() => {
        steuerung.current?.oeffnen();
        weiter.current?.focus();
    }, []);
    return (_jsx(Dialog, { ref: steuerung, offen: true, strikt: true, unter: "stapel", breite: 480, titel: _jsx("span", { ref: anker, children: "\u00C4nderungen verwerfen?" }), fuss: (_jsxs(_Fragment, { children: [_jsx("button", { ref: weiter, type: "button", className: "ki-modal__knopf", onClick: p.onWeiterBearbeiten, children: "Weiter bearbeiten" }), _jsx("button", { type: "button", className: "ki-modal__primaer", onClick: p.onVerwerfen, children: "Verwerfen" })] })), children: _jsx("p", { className: "ki-rueckfrage__text", children: WORTLAUT }) }));
}
//# sourceMappingURL=VerwerfenRueckfrage.js.map